/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import Modal from '~/components/Modal'
import { useServiceContext } from '~/root'
import FsiPicker from '~/components/FsiPicker/FsiPicker'
import AddDocumentModal from '~/pages/browser/add-document/add-new-document/AddDocumentModal'
import { useModal } from '~/contexts/ModalContext'
import { useTranslation } from 'react-i18next'
import FileIcon from '~/components/FileIcon'
import { File as FileConstants } from '~/constants/file'
import PageWrapper from '../edit-document/PageWrapper'

interface ReclassifyFsiModalProps {
  fsiId: number | string
  onClose?: () => void
}

const ReclassifyFsiModal: React.FC<ReclassifyFsiModalProps> = ({ fsiId, onClose }) => {
  const context = useServiceContext()
  const { api, $rootScope } = context
  const { translate } = context.service.filter
  const { t } = useTranslation()

  // Local excerpt function - not available in service.filter
  const excerpt = (text: string, stripHtml: boolean, length: number) => {
    if (!text) return ''
    const truncated = text.length > length ? text.substring(0, length) + '...' : text
    return stripHtml ? truncated.replace(/<[^>]*>/g, '') : truncated
  }

  const { showModal } = useModal()
  const [isOpen, setIsOpen] = useState(true)
  const [file, setFile] = useState<any>(null)
  const [selectedDocument, setSelectedDocument] = useState<any>(null)
  const [isFolderSelected, setIsFolderSelected] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  useEffect(() => {
    // Convert fsiId to number if it's a string
    const id = typeof fsiId === 'string' ? parseInt(fsiId, 10) : fsiId
    context.api.files.getDetails(id).then((data: any) => {
      setFile(data)
    })
  }, [fsiId])

  const submit = () => {
    setIsSubmitted(true)
    if (selectedDocument) {
      const params = {
        reclassify: true,
        file: file
      }

      if (selectedDocument?.type === FileConstants.type.FOLDER) {
        // Show AddDocumentModal for folders
        showModal(<AddDocumentModal params={params} parentId={selectedDocument?.id} />)
      } else {
        showModal(
          <PageWrapper
            params={params}
            fileId={selectedDocument?.id}
            title={'BROWSER_RECLASSIFY_AS_PLACEHOLDER_H_TITLE'}
          />
        )
      }
    }
  }

  const cancel = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  return (
    <Modal
      show={isOpen}
      handleClose={cancel}
      className="reclassify-modal-wrapper"
      size="lg"
      title={translate('BROWSER_RECLASSIFY_FSI_H_TITLE')}
    >
      <Modal.Body>
        <div className="reclassify-fsi">
          <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>{translate('FSI_TYPE_DOCUMENT')}</Form.Label>
          <p className="reclassify-field-spacer" style={{ fontSize: '12px' }}>
            {file?.name}
          </p>

          <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>{translate('DOC_TYPE')}</Form.Label>
          <p className="reclassify-field-spacer" style={{ fontSize: '12px' }}>
            {file?.fsiTypeTemplate?.name}
          </p>

          <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>{translate('EXISTING_FOLDER')}</Form.Label>
          <p className="reclassify-field-spacer" style={{ fontSize: '12px' }}>
            {excerpt(file?.parent?.name, false, 50)}
          </p>

          <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>
            {translate('SELECT_DESTIONATION_FOLDER_OR_PLACEHOLDER')}
          </Form.Label>
          <div className="d-flex align-items-center justify-content-start reclassify-field-spacer">
            {selectedDocument ? (
              <>
                <FileIcon
                  fileName={selectedDocument?.name || ''}
                  fileType={selectedDocument?.type || 'DOCUMENT'}
                  isValid={true}
                  isObsolete={false}
                />
                <span className="selected-file-name">{selectedDocument?.name}</span>
              </>
            ) : (
              <span className="selected-file-name">{translate('NOT_AVAILABLE')}</span>
            )}
          </div>

          <div className="fsi-pickere-wrapper fs12">
            <FsiPicker
              options={{
                extendParams: {
                  onlyFoldersAndPlaceholders: true
                },
                showRC: true,
                showSystem: true
              }}
              onSelect={(fsi: any) => {
                setIsFolderSelected(true)
                setSelectedDocument(fsi)
              }}
            />
            {!isFolderSelected && isSubmitted && (
              <div className="select-folder-error error">{translate('RECLASSIFY_FORM_SELECT_FSI_MSG_ERROR')}</div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div>
          <Button data-action={'cancel'} variant="link" className="text-capitalize" onClick={cancel} type="button">
            {translate('CANCEL')}
          </Button>
          <Button data-action={'ok'} className="btn btn-primary text-capitalize" type="button" onClick={submit}>
            {translate('NEXT_BUTTON')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default ReclassifyFsiModal
